/**
 * Silent re-mint of a Gmail access token via Google Identity Services (GIS), against the standing
 * grant a recruiter gave through the Firebase popup. One of the three token sources behind
 * useGmailToken.ts; see the ordering and the reasoning there.
 */
import { EXPIRY_SLACK_MS, GMAIL_SCOPES, type Grant } from './gmailGrant';

/**
 * The OAuth client Firebase Auth's Google provider uses (finn-agentspace). A public identifier,
 * hardcoded the same way src/firebase.ts hardcodes its config. It MUST be this client and not a
 * new one: the recruiter's standing scope grant is attached to it, which is what lets GIS mint
 * tokens without interaction.
 */
export const GOOGLE_OAUTH_CLIENT_ID =
  '520260363689-eec4j63oc5dql6froo00kub4ldvfv96s.apps.googleusercontent.com';

/**
 * The GIS silent mint works only when this page's origin is registered as an Authorized
 * JavaScript origin on the OAuth client above — a GCP console step that is still outstanding.
 * Until it is done, attempting GIS is strictly worse than skipping it: requestAccessToken opens a
 * real window that Google fills with "Access blocked: Error 400 origin_mismatch", which reads as
 * a broken login — and it spends the click's user activation, so the Firebase-popup fallback can
 * then be blocked. Flip VITE_GIS_SILENT_MINT=true only AFTER registering the
 * origins (people-space.finn.com, people-space-prod.web.app, the localhost dev ports). Read
 * lazily so tests can stub it per case.
 */
export const gisSilentMintEnabled = () => import.meta.env.VITE_GIS_SILENT_MINT === 'true';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
/** Give up on a silent mint that hangs (script or Google session issues) rather than spin. */
const SILENT_TIMEOUT_MS = 15_000;

interface GisTokenResponse {
  access_token?: string;
  expires_in?: number | string;
  /** Space-separated scopes Google actually GRANTED — may be narrower than what was requested. */
  scope?: string;
  error?: string;
}

interface GisTokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            hint?: string;
            prompt?: string;
            callback: (response: GisTokenResponse) => void;
            error_callback?: (error: unknown) => void;
          }) => GisTokenClient;
        };
      };
    };
  }
}

let gisLoading: Promise<void> | null = null;

/** Load the GIS script once, on demand — only visitors of this tab pay for it. */
function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (!gisLoading) {
    gisLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GIS_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        gisLoading = null; // a network blip should not disable GIS for the whole page lifetime
        reject(new Error('Google Identity Services failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return gisLoading;
}

/**
 * Mint an access token against the recruiter's standing grant, without interaction.
 *
 * Resolves null on ANY failure — no standing grant, origin not registered on the OAuth client,
 * popup suppressed, script unavailable, timeout — because every failure has the same correct
 * answer: fall back to the Firebase popup.
 */
export function silentMint(userEmail: string): Promise<Grant | null> {
  const owner = userEmail.trim().toLowerCase();
  return loadGis().then(
    () =>
      new Promise((resolve) => {
        const oauth2 = window.google?.accounts?.oauth2;
        if (!oauth2) return resolve(null);

        let settled = false;
        const settle = (value: Grant | null) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        };
        const timer = setTimeout(() => settle(null), SILENT_TIMEOUT_MS);

        try {
          const client = oauth2.initTokenClient({
            client_id: GOOGLE_OAUTH_CLIENT_ID,
            scope: GMAIL_SCOPES.join(' '),
            // Steers Google to the signed-in account; the proxy's verifyMailboxOwner is what
            // actually rejects a token for the wrong mailbox.
            hint: owner,
            prompt: '',
            callback: (response) => {
              if (!response?.access_token) return settle(null);
              const lifetimeMs = (Number(response.expires_in) || 3600) * 1000;
              settle({
                token: response.access_token,
                owner,
                expiresAt: Date.now() + lifetimeMs - EXPIRY_SLACK_MS,
                /**
                 * What Google GRANTED, not what we asked for — GIS reports it, so use it. A
                 * standing grant from before the gmail.modify escalation silently mints a token
                 * WITHOUT the new scope; stamping the request list onto it would record a lie,
                 * and connect() would accept a token that every write is going to bounce.
                 */
                scopes: typeof response.scope === 'string' && response.scope
                  ? response.scope.split(' ')
                  : [...GMAIL_SCOPES],
                source: 'gis',
              });
            },
            error_callback: () => settle(null),
          });
          client.requestAccessToken({ prompt: '' });
        } catch {
          settle(null);
        }
      }),
    () => null
  );
}
